import HelpPopup from '@wandb/weave/common/components/elements/HelpPopup';
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import Input from '@wandb/weave/common/components/Input';
import {
  RepresentationType,
  RepresentationTypeValues,
} from '@wandb/weave/common/types/libs/nglviewer';
import {MediaCardType} from '@wandb/weave/common/types/media';
import {Slider} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {debounce, isArray} from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox, Dropdown, Form, Message, Tab} from 'semantic-ui-react';

import {
  DEFAULT_LINE_PLOT_MAX_NUM_RUNS,
  DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  mediaSliderMetricKeyRemoved,
  mediaSliderMetricKeySelected,
} from '../../services/analytics/productEvents';
import {useViewer} from '../../state/viewer/hooks';
import {useRampFlagMediaPanelSliderKey} from '../../util/rampFeatureFlags';
import {makeOptions, Option} from '../../util/uihelpers';
import {Banner} from '../Banner';
import {Axis, MediaBrowserPanelConfig} from './common';
import {
  MediaBrowserPanelProps,
  MediaConfigWithDefaults,
  SizingSettings,
} from './Component';
import {MAX_RUNS_PERF_WARNING_THRESHOLD} from './config';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';

const AxisStrings: Axis[] = ['step', 'index', 'run', 'camera'];

export type ColumnContentOption = {
  text: string;
  key: Axis;
  value: Axis;
};

export function getAxisOptions(
  multiRun: boolean | undefined
): ColumnContentOption[] {
  return AxisStrings.filter(s => {
    // When in not in multi run mode remove the run options
    return !multiRun && s === 'run' ? false : true;
  })
    .filter(s => {
      //           Performance tuning required
      //           This requires adding a cache to useLoadFile so it reuses
      //           the same blob between cards and adding something to the
      //           babylon renderer to reuse the same scene object.
      // return mediaType !== 'object3D' && s === 'camera' ? false : true;
      return s !== 'camera';
    })
    .map(s => {
      let text;
      if (s === 'step') {
        text = 'Log Step';
      } else if (s === 'index') {
        text = 'Example Index';
      } else if (s === 'run') {
        text = 'Run';
      } else {
        text = s;
      }

      return {text, key: s, value: s};
    });
}

const PanelSettingsComp = (props: {
  config: MediaConfigWithDefaults;
  mediaType: MediaCardType | null;
  multiRun?: boolean;
  allMediaKeys: string[];
  numericKeys?: string[] | undefined;
  sizingSettings: SizingSettings;
  historySampleRate?: number;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
}) => {
  const {allMediaKeys, numericKeys, mediaType, updateConfig, sizingSettings} =
    props;
  const {config} = props;

  const {entityName, projectName, panelId} = useParams<{
    entityName: string;
    projectName: string;
    panelId: string;
  }>();
  const user = useViewer()?.username;
  const isMediaSliderKeyEnabled = useRampFlagMediaPanelSliderKey(entityName);
  const currentMediaSliderKey = useMediaSliderKey(config.mediaSliderKey);
  const trackMediaSliderKeyChange = useCallback(
    (updatedValue: string) => {
      const isCurrentValueDefault = currentMediaSliderKey === '_step';
      const isNewValueDefault = updatedValue === '_step';
      const eventData = {
        entityName,
        projectName,
        user,
        panelId,
      };
      if (!isNewValueDefault) {
        mediaSliderMetricKeySelected({
          ...eventData,
          metricKey: updatedValue,
        });
      } else if (!isCurrentValueDefault && isNewValueDefault) {
        mediaSliderMetricKeyRemoved({
          previousMetricKey: currentMediaSliderKey,
          ...eventData,
        });
      }
    },
    [currentMediaSliderKey, entityName, projectName, panelId, user]
  );
  const updateConfigDebounced = useMemo(
    () => debounce(updateConfig, 500),
    [updateConfig]
  );

  const [maxRunsDebouncedVal, setMaxRunsDebouncedVal] = useState(
    config.maxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW
  );

  const updateMaxRuns = useCallback(
    (value: number) => {
      if (value > 0 && value <= DEFAULT_LINE_PLOT_MAX_NUM_RUNS) {
        setMaxRunsDebouncedVal(value);
        updateConfigDebounced({maxRuns: value});
      }
    },
    [updateConfigDebounced]
  );

  // Make filter options
  const axisOptions = getAxisOptions(props.multiRun);

  const xAxis = config.gridSettings && config.gridSettings.xAxis;
  const yAxis = config.gridSettings && config.gridSettings.yAxis;
  const galleryAxis = config.gallerySettings && config.gallerySettings.axis;
  const sliderKeyOptions: Option[] = useMemo(
    () => numericKeys?.map(k => ({value: k, text: k})) ?? [],
    [numericKeys]
  );

  const basicTab = (
    <div>
      <Form className="settings__section">
        <div className="panel-setting__item panel-setting__item--stacked">
          <h5 className="input-label">Chart title</h5>
          <Input
            data-test="panel-chart-title-input"
            fluid
            value={config.chartTitle || ''}
            onChange={debounce((e, {value}) => {
              updateConfig({
                chartTitle: value,
              });
            })}
          />
        </div>

        {
          <div className="panel-setting__item panel-setting__item--stacked">
            <h5 className="input-label">Media key(s)</h5>
            <Form.Field className="panel-setting">
              <ModifiedDropdown
                style={{width: '100%'}}
                data-test="media-key-selector"
                placeholder="Select Media Key"
                multiple
                enableReordering
                search
                selection
                options={allMediaKeys.map(k => {
                  return {key: k, value: k, text: k};
                })}
                value={config.mediaKeys}
                onChange={(e, {value}) => {
                  // Convert singletons to array
                  const updateValue = isArray(value)
                    ? (value as string[])
                    : [value as string];

                  updateConfig({mediaKeys: updateValue});
                }}
              />
            </Form.Field>
          </div>
        }
        {isMediaSliderKeyEnabled && (
          <div className="panel-setting__item panel-setting__item--stacked">
            <h5 className="input-label">Slider key</h5>
            <Form.Field
              className="panel-setting"
              data-testid="slider-media-key-select">
              <div style={{width: '100%', position: 'relative'}}>
                <ModifiedDropdown
                  debounceTime={300}
                  itemLimit={10}
                  onChange={(e, {value: newValue}) => {
                    const updatedValue = newValue
                      ? (newValue as string)
                      : '_step';
                    updateConfig({mediaSliderKey: updatedValue});
                    trackMediaSliderKeyChange(updatedValue);
                  }}
                  options={sliderKeyOptions}
                  placeholder="Slider key"
                  resultLimit={10}
                  search
                  selection
                  style={{width: '100%'}}
                  value={config.mediaSliderKey ?? '_step'}
                />
              </div>
            </Form.Field>
          </div>
        )}

        <Tailwind>
          <fieldset className="mt-8">
            <legend
              id="max-runs-label"
              className="mb-5 block text-sm text-moon-800">
              Max runs to include
            </legend>
            <div className="grid grid-cols-[1fr_auto] items-center gap-12">
              <Slider.Root
                aria-labelledby="max-runs-label"
                min={1}
                max={DEFAULT_LINE_PLOT_MAX_NUM_RUNS}
                step={1}
                value={[maxRunsDebouncedVal]}
                onValueChange={newVal => updateMaxRuns(newVal[0])}>
                <Slider.Track>
                  <Slider.Range />
                </Slider.Track>
                <Slider.Thumb />
              </Slider.Root>
              <Slider.Display
                data-test="max-runs-slider-input"
                aria-labelledby="max-runs-label"
                min={1}
                max={DEFAULT_LINE_PLOT_MAX_NUM_RUNS}
                step={1}
                value={maxRunsDebouncedVal}
                onChange={updateMaxRuns}
              />
            </div>
          </fieldset>
          {maxRunsDebouncedVal >= MAX_RUNS_PERF_WARNING_THRESHOLD && (
            <Banner
              className="my-12 flex items-start justify-start gap-8 p-12 text-sm"
              variant="info">
              <Icon name="info" className="mt-1 h-18 w-18 shrink-0" />
              <p>
                Including a large number of runs may affect performance and
                usability.
              </p>
            </Banner>
          )}
        </Tailwind>

        {sizingSettings.columnCount.max !== sizingSettings.columnCount.min &&
          !config.actualSize &&
          !config.fitToDimension && (
            <>
              <div
                data-test="column-count-input"
                className="panel-setting__item panel-setting__item--stacked">
                <h5 className="input-label">Columns</h5>
                <SliderInput
                  sliderInPopup
                  min={sizingSettings.columnCount.min}
                  max={sizingSettings.columnCount.max}
                  minLabel={sizingSettings.columnCount.min.toString()}
                  maxLabel={sizingSettings.columnCount.max.toString()}
                  step={1}
                  value={sizingSettings.columnCount.current}
                  debounceTime={50}
                  hasInput
                  onChange={configValue =>
                    updateConfig({columnCount: configValue})
                  }
                />
              </div>
              {config.mode === 'gallery' ? (
                <div
                  data-test="max-item-count-input"
                  className="panel-setting__item panel-setting__item--stacked">
                  <h5 className="input-label">
                    Max item count
                    <HelpPopup helpText="The maximum number of items that will be display in the panel" />
                  </h5>
                  <SliderInput
                    sliderInPopup
                    min={0}
                    max={108}
                    minLabel={'0'}
                    maxLabel={'108'}
                    step={1}
                    value={config.maxGalleryItems}
                    debounceTime={50}
                    hasInput
                    onChange={configValue =>
                      updateConfig({maxGalleryItems: configValue})
                    }
                  />
                </div>
              ) : (
                <div
                  className="panel-setting__item panel-setting__item--stacked"
                  data-test="max-rows-input">
                  <h5 className="input-label">Rows</h5>
                  <SliderInput
                    sliderInPopup
                    min={0}
                    max={50}
                    minLabel={'0'}
                    maxLabel={'50'}
                    step={1}
                    value={config.maxYAxisCount}
                    debounceTime={50}
                    hasInput
                    onChange={configValue =>
                      updateConfig({maxYAxisCount: configValue})
                    }
                  />
                </div>
              )}
            </>
          )}

        {(mediaType === 'image' || mediaType === 'video') &&
          config.mode !== 'grid' && (
            <>
              <div
                data-test="original-size-checkbox"
                className="panel-setting__item"
                onClick={() =>
                  updateConfig({
                    actualSize: !config.actualSize,
                    fitToDimension: false,
                  })
                }>
                <Checkbox toggle checked={config.actualSize} />
                <span className="panel-setting__toggle-label">
                  Original size
                </span>
              </div>
              {mediaType === 'image' && (
                <div
                  data-test="fit-to-one-dimension-checkbox"
                  className="panel-setting__item"
                  onClick={() =>
                    updateConfig({
                      fitToDimension: !config.fitToDimension,
                      actualSize: false,
                    })
                  }>
                  <Checkbox toggle checked={config.fitToDimension} />
                  <span className="panel-setting__toggle-label">
                    Fit to one dimension
                    <HelpPopup helpText="Fit one dimension of your media to the panel and scroll along the other one." />
                  </span>
                </div>
              )}
            </>
          )}
      </Form>
    </div>
  );

  // Actions
  const swapMode = () => {
    if (config.mode === 'grid') {
      updateConfig({mode: 'gallery'});
    } else {
      updateConfig({mode: 'grid', actualSize: false, fitToDimension: false});
    }
  };

  const advancedTab = (
    <>
      {mediaType === 'image' && (
        <div
          data-test="smooth-image-checkbox"
          className="panel-setting__item"
          onClick={() => updateConfig({pixelated: !config.pixelated})}>
          <Checkbox toggle checked={!config.pixelated} />
          <span className="panel-setting__toggle-label">Smooth image</span>
        </div>
      )}

      <div
        data-test="grid-mode-checkbox"
        className="panel-setting__item"
        onClick={swapMode}>
        <Checkbox
          disabled={
            config.actualSize === true || config.fitToDimension === true
          }
          toggle
          checked={config.mode === 'grid'}
        />
        <span className="panel-setting__toggle-label">Grid mode</span>
      </div>

      {config.mode === 'gallery' && (
        <>
          <h5 className="input-label">Columns</h5>
          <ModifiedDropdown
            data-test="column-selector"
            placeholder="Columns"
            options={axisOptions}
            value={galleryAxis}
            selection
            onChange={(e, {value}) => {
              // Convert singletons to array
              const axis = value as Axis;

              updateConfig({
                gallerySettings: {
                  axis,
                },
              });
            }}
          />
        </>
      )}

      <div
        data-test="step-stride-length-input"
        className="panel-setting__item panel-setting__item--stacked">
        <h5 className="input-label">Stride length</h5>
        <Input
          value={config.stepStrideLength}
          type="number"
          onChange={(e, {value}) => {
            updateConfig({
              stepStrideLength: parseInt(value, 0),
            });
          }}
        />
      </div>

      <div
        className="panel-setting__item"
        data-test="snap-to-existing-step-checkbox"
        onClick={() => {
          updateConfig({snapToExistingStep: !config.snapToExistingStep});
        }}>
        <Checkbox toggle checked={config.snapToExistingStep} />
        <span className="panel-setting__toggle-label">
          Snap to existing step
        </span>
      </div>

      {config.mode === 'grid' && (
        <>
          <div className="panel-setting__item panel-setting__item--stacked">
            <h5 className="input-label">X-Axis</h5>
            <ModifiedDropdown
              data-test="media-key-selector"
              placeholder="X-Axis"
              // Hide the currently selected option in the counter axis
              options={axisOptions}
              value={xAxis}
              selection
              onChange={(e, {value}) => {
                // Convert singletons to array
                const axis = value as Axis;

                // When the same axis is picked for both cause a swap
                if (axis === yAxis) {
                  updateConfig({
                    gridSettings: {
                      ...config.gridSettings,
                      yAxis: xAxis,
                      xAxis: axis,
                    },
                  });
                } else {
                  updateConfig({
                    gridSettings: {
                      ...config.gridSettings,
                      xAxis: axis,
                    },
                  });
                }
              }}
            />
          </div>
          <div className="panel-setting__item panel-setting__item--stacked">
            <h5 className="input-label">Y-Axis</h5>
            <ModifiedDropdown
              data-test="media-key-selector"
              placeholder="Row"
              options={axisOptions}
              value={yAxis}
              selection
              onChange={(e, {value}) => {
                const axis = value as Axis;

                // When the same axis is picked for both cause a swap
                if (axis === xAxis) {
                  updateConfig({
                    gridSettings: {
                      ...config.gridSettings,
                      yAxis: axis,
                      xAxis: yAxis,
                    },
                  });
                } else {
                  updateConfig({
                    gridSettings: {
                      ...config.gridSettings,
                      yAxis: axis,
                    },
                  });
                }
              }}
            />
          </div>
        </>
      )}
      {props.historySampleRate && props.historySampleRate !== 1 && (
        <Message>
          {`Log Steps are sampled at a rate of ~${parseFloat(
            props.historySampleRate.toString()
          ).toFixed(3)} steps per sample`}
        </Message>
      )}
    </>
  );
  const baseTabs = [
    {
      menuItem: 'Basic',
      render: () => (
        <Tab.Pane as="div" className="form-grid">
          {basicTab}
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Advanced',
      render: () => (
        <Tab.Pane as="div" className="form-grid">
          {advancedTab}
        </Tab.Pane>
      ),
    },
  ];

  const moleculeTab = {
    menuItem: 'Molecular',
    render: () => {
      const mConfig = config.moleculeConfig || {};

      const updateMConfig = (
        newPartialConfig: Partial<MediaBrowserPanelConfig['moleculeConfig']>
      ) =>
        updateConfig({
          moleculeConfig: {
            ...mConfig,
            ...newPartialConfig,
          },
        });

      const {representation} = mConfig;

      return (
        <Tab.Pane as="div" className="form-grid">
          Representation Style
          <Dropdown
            value={representation ?? 'default'}
            options={makeOptions(RepresentationTypeValues)}
            onChange={(e, v) => {
              if (typeof v.value === 'string') {
                updateMConfig({
                  representation: v.value as RepresentationType,
                });
              }
            }}
          />
        </Tab.Pane>
      );
    },
  };
  const tabs = baseTabs;

  if (mediaType === 'molecule') {
    tabs.push(moleculeTab);
  }
  return (
    <Tab
      panes={tabs}
      menu={{
        secondary: true,
        pointing: true,
        className: 'chart-settings-tab-menu',
      }}
    />
  );
};
export const PanelSettings = React.memo(PanelSettingsComp);
